import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { supabase } from "@/lib/supabase";
import {
  clearPasswordRecoveryPending,
  markPasswordRecoveryPending,
} from "@/lib/password-recovery";
import { TRPCClientError } from "@trpc/client";
import { keepPreviousData } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getAuthProviderMismatchMessage } from "@shared/auth-provider";
import {
  isServerSessionConfirmed,
  nextAuthUserSnapshot,
  readStoredAuthUserSnapshot,
  writeStoredAuthUserSnapshot,
} from "@/lib/supabase-session-token";

type UseAuthOptions = {
  redirectOnUnauthenticated?: boolean;
  redirectPath?: string;
};

export function useAuth(options?: UseAuthOptions) {
  const getDefaultRedirectPath = () => {
    const loginUrl = getLoginUrl();
    return loginUrl || "/login";
  };
  
  const { redirectOnUnauthenticated = false, redirectPath = getDefaultRedirectPath() } =
    options ?? {};
  const utils = trpc.useUtils();
  const [storedUser, setStoredUser] = useState(() =>
    typeof localStorage === "undefined" ? null : readStoredAuthUserSnapshot(localStorage),
  );

  const meQuery = trpc.auth.me.useQuery(undefined, {
    retry: (failureCount, error) => {
      if (getAuthProviderMismatchMessage(error)) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 60_000,
    refetchInterval: false,
    placeholderData: keepPreviousData,
  });

  // Log auth state changes
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (meQuery.data) {
      console.log("[Auth] User authenticated:", meQuery.data.email, meQuery.data.name);
    } else if (meQuery.error) {
      const errorMsg = meQuery.error.message || "";
      console.error("[Auth] Error fetching user:", {
        message: errorMsg,
        cookies: document.cookie,
        hasSessionCookie: document.cookie.includes("app_session_id"),
      });
    } else if (!meQuery.isLoading && !meQuery.isFetching) {
      console.log("[Auth] No user data (not authenticated)");
    }
  }, [meQuery.data, meQuery.error, meQuery.isLoading, meQuery.isFetching]);

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      utils.auth.me.setData(undefined, null);
    },
  });

  const logout = useCallback(async () => {
    try {
      if (supabase) {
        await supabase.auth.signOut();
      }
      await logoutMutation.mutateAsync();
    } catch (error: unknown) {
      if (
        error instanceof TRPCClientError &&
        error.data?.code === "UNAUTHORIZED"
      ) {
        return;
      }
      throw error;
    } finally {
      utils.auth.me.setData(undefined, null);
      setStoredUser(null);
      if (typeof localStorage !== "undefined") {
        writeStoredAuthUserSnapshot(localStorage, null);
      }
      await utils.auth.me.invalidate();
    }
  }, [logoutMutation, utils]);

  // Keep the server "me" query in sync with Supabase auth state changes
  // (sign-in via OAuth callback, token refresh, sign-out in another tab).
  useEffect(() => {
    if (!supabase) return;
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        markPasswordRecoveryPending();
        utils.auth.me.invalidate();
        return;
      }
      if (event === "SIGNED_OUT") {
        void supabase.auth.getSession().then(({ data }) => {
          if (data.session?.access_token) {
            void utils.auth.me.invalidate();
            return;
          }
          clearPasswordRecoveryPending();
          utils.auth.me.setData(undefined, null);
          setStoredUser(null);
          if (typeof localStorage !== "undefined") {
            writeStoredAuthUserSnapshot(localStorage, null);
          }
          void utils.auth.me.invalidate();
        });
        return;
      }
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        void utils.auth.me.invalidate();
      }
    });

    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      void supabase.auth.getSession().then(({ data }) => {
        if (data.session?.access_token) void utils.auth.me.invalidate();
      });
    };
    window.addEventListener("pageshow", onPageShow);

    return () => {
      data.subscription.unsubscribe();
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [utils]);

  useEffect(() => {
    const mismatch = getAuthProviderMismatchMessage(meQuery.error);
    if (!mismatch || !supabase) return;
    void supabase.auth.signOut();
  }, [meQuery.error]);

  useEffect(() => {
    if (meQuery.isLoading) return;
    setStoredUser(meQuery.data ?? null);
    if (typeof localStorage !== "undefined") {
      writeStoredAuthUserSnapshot(localStorage, meQuery.data ?? null);
    }
  }, [meQuery.data, meQuery.isLoading]);

  const state = useMemo(() => {
    const user = nextAuthUserSnapshot(
      meQuery.data ?? null,
      storedUser as typeof meQuery.data,
      meQuery.isLoading,
    );
    const isLoading = logoutMutation.isPending || (meQuery.isLoading && !user);

    return {
      user,
      loading: isLoading,
      error: meQuery.error ?? logoutMutation.error ?? null,
      isAuthenticated: Boolean(user),
      sessionConfirmed: isServerSessionConfirmed(meQuery),
    };
  }, [
    meQuery.data,
    meQuery.error,
    meQuery.isFetched,
    meQuery.isLoading,
    storedUser,
    logoutMutation.error,
    logoutMutation.isPending,
  ]);

  useEffect(() => {
    if (!redirectOnUnauthenticated) return;
    if (meQuery.isLoading || logoutMutation.isPending) return;
    if (state.user) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname === redirectPath) return;

    window.location.href = redirectPath;
  }, [
    redirectOnUnauthenticated,
    redirectPath,
    logoutMutation.isPending,
    meQuery.isLoading,
    state.user,
  ]);

  return {
    ...state,
    refresh: () => meQuery.refetch(),
    logout,
  };
}
